import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase-config';

function ContactForm({ prefilledPackage = null }) {
  const { i18n } = useTranslation();
  const currentLang = i18n.language || 'bs';
  
  const [form, setForm] = useState({ 
    name: '', 
    email: '', 
    phone: '',
    weddingDate: '',
    message: '',
    contactMethods: []
  });
  
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });

  // Prefill message when package is selected
  useEffect(() => {
    if (prefilledPackage) {
      const packageName = typeof prefilledPackage.naziv === 'object' 
        ? prefilledPackage.naziv[currentLang] 
        : prefilledPackage.naziv;
      
      const prefilledMessage = currentLang === 'bs'
        ? `Zdravo! Zainteresovan/a sam za paket "${packageName}". Molim Vas za više informacija i dostupnost.`
        : `Hello! I'm interested in the "${packageName}" package. Please send me more information and availability.`;
      
      setForm(prev => ({ ...prev, message: prefilledMessage }));
      
      // Scroll to contact form
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [prefilledPackage, currentLang]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleDateChange = (e) => {
    const date = e.target.value;
    setForm({ ...form, weddingDate: date });
  };

  const handleContactMethodChange = (method) => {
    setForm(prev => {
      const methods = prev.contactMethods.includes(method)
        ? prev.contactMethods.filter(m => m !== method)
        : [...prev.contactMethods, method];
      return { ...prev, contactMethods: methods };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setStatus({ type: '', message: '' });

    try {
      // Telegram from ENV
      const telegramBotToken = process.env.REACT_APP_TELEGRAM_BOT_TOKEN;
      const telegramChatId = process.env.REACT_APP_TELEGRAM_CHAT_ID;

      // Save message to Firebase
      const messageData = {
        name: form.name,
        email: form.email,
        phone: form.phone || '',
        weddingDate: form.weddingDate || '',
        message: form.message,
        contactMethods: form.contactMethods || [],
        packageName: prefilledPackage ? (typeof prefilledPackage.naziv === 'object' ? prefilledPackage.naziv[currentLang] : prefilledPackage.naziv) : '',
        status: 'new', // new, read, replied
        createdAt: serverTimestamp(),
        language: currentLang
      };

      await addDoc(collection(db, "messages"), messageData);
      
      // Send Telegram notification if configured in .env
      if (telegramBotToken && telegramChatId && !telegramBotToken.includes('your_')) {
        try {
          const telegramText = `
🔔 *Nova poruka sa web stranice*

👤 *Ime:* ${messageData.name}
📧 *Email:* ${messageData.email}
📞 *Telefon:* ${messageData.phone || '/'}
📅 *Datum:* ${messageData.weddingDate || '/'}
📦 *Paket:* ${messageData.packageName || '/'}
💬 *Poruka:*
${messageData.message}

🌐 *Jezik:* ${messageData.language === 'bs' ? 'Bosanski' : 'Engleski'}
          `.trim();

          const teleRes = await fetch(`https://api.telegram.org/bot${telegramBotToken}/sendMessage`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              chat_id: telegramChatId,
              text: telegramText,
              parse_mode: 'Markdown'
            })
          });

          if (!teleRes.ok) {
            const errorInfo = await teleRes.json();
            console.error('Telegram notification error details:', errorInfo);
          }
        } catch (teleErr) {
          console.error('Telegram notification network error:', teleErr);
        }
      }
      
      setStatus({ 
        type: 'success', 
        message: currentLang === 'bs' 
          ? '✅ Poruka uspješno poslana! Javićemo Vam se uskoro.' 
          : '✅ Message sent successfully! We will contact you soon.'
      });
      
      // Reset form
      setForm({ name: '', email: '', phone: '', weddingDate: '', message: '', contactMethods: [] });
      
    } catch (error) {
      console.error('Email send error:', error);
      setStatus({ 
        type: 'error', 
        message: currentLang === 'bs'
          ? '❌ Greška pri slanju poruke. Molimo pokušajte ponovo.'
          : '❌ Error sending message. Please try again.'
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white rounded-[40px] shadow-2xl border border-[#ede3da] p-4 md:p-12 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-[#bfa46d]/5 to-transparent rounded-bl-full"></div>
      <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-amber-200/5 to-transparent rounded-tr-full"></div>
      
      <div className="relative z-10">
        <div className="mb-8">
          <h3 className="text-2xl font-bold text-gray-900 font-serif mb-2">
            {currentLang === 'bs' ? 'Pošaljite nam poruku' : 'Send us a message'}
          </h3>
          <p className="text-gray-500 text-sm">
            {currentLang === 'bs' 
              ? 'Popunite formu i kontaktirat ćemo Vas što prije' 
              : 'Fill out the form and we will contact you as soon as possible'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 font-sans">
          <div className="space-y-2">
            <label htmlFor="name" className="text-[10px] font-bold text-gray-700 tracking-[0.2em] block">
              {currentLang === 'bs' ? 'Ime i prezime *' : 'Full name *'}
            </label>
            <input
              type="text"
              id="name"
              name="name"
              placeholder={currentLang === 'bs' ? 'Unesite vaše ime' : 'Enter your name'}
              value={form.name}
              onChange={handleChange}
              required
              className="w-full px-5 py-4 bg-[#fcfaf8] border border-[#ede3da] rounded-2xl focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-transparent transition-all text-gray-900 placeholder-gray-400"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="email" className="text-[10px] font-bold text-gray-700 tracking-[0.2em] block">
                {currentLang === 'bs' ? 'Email adresa *' : 'Email address *'}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder={currentLang === 'bs' ? 'vas.email@example.com' : 'your.email@example.com'}
                value={form.email}
                onChange={handleChange}
                required
                className="w-full px-5 py-4 bg-[#fcfaf8] border border-[#ede3da] rounded-2xl focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-transparent transition-all text-gray-900 placeholder-gray-400"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="phone" className="text-[10px] font-bold text-gray-700 tracking-[0.2em] block">
                {currentLang === 'bs' ? 'Broj telefona' : 'Phone number'}
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                placeholder="+387 61 000 000"
                value={form.phone}
                onChange={handleChange}
                className="w-full px-5 py-4 bg-[#fcfaf8] border border-[#ede3da] rounded-2xl focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-transparent transition-all text-gray-900 placeholder-gray-400"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="weddingDate" className="text-[10px] font-bold text-gray-700 tracking-[0.2em] block">
              {currentLang === 'bs' ? 'Datum vjenčanja' : 'Wedding date'}
            </label>
            <input
              type="date"
              id="weddingDate"
              name="weddingDate"
              value={form.weddingDate}
              onChange={handleDateChange}
              min={new Date().toISOString().split('T')[0]}
              className="w-full px-5 py-4 bg-[#fcfaf8] border border-[#ede3da] rounded-2xl focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-transparent transition-all text-gray-900"
            />
          </div>

          {form.phone && (
            <div className="space-y-3 p-6 bg-gradient-to-br from-[#fcfaf8] to-white rounded-3xl border border-[#ede3da]">
              <label className="text-[10px] font-bold text-gray-700 tracking-[0.2em] block">
                {currentLang === 'bs' 
                  ? 'Kako želite da vas kontaktiramo? (opciono)' 
                  : 'How would you like us to contact you? (optional)'}
              </label>
              <div className="flex flex-wrap gap-3">
                {['email', 'viber', 'whatsapp', 'telegram', 'sms'].map((method) => {
                  const icons = {
                    email: '📧',
                    viber: '💜',
                    whatsapp: '💚',
                    telegram: '✈️',
                    sms: '📱'
                  };
                  return (
                    <label key={method} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 cursor-pointer transition-all ${
                      form.contactMethods.includes(method) 
                        ? 'bg-gray-50 border-gray-900 text-gray-900 shadow-sm' 
                        : 'bg-white border-[#ede3da] text-gray-600 hover:border-gray-900'
                    }`}>
                      <input
                        type="checkbox"
                        checked={form.contactMethods.includes(method)}
                        onChange={() => handleContactMethodChange(method)}
                        className="hidden"
                      />
                      <span>{icons[method]}</span>
                      <span className="text-sm font-bold capitalize">{method}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor="message" className="text-[10px] font-bold text-gray-700 tracking-[0.2em] block">
              {currentLang === 'bs' ? 'Vaša poruka *' : 'Your message *'}
            </label>
            <textarea
              id="message"
              name="message"
              rows="6"
              placeholder={currentLang === 'bs' 
                ? 'Opišite vaše potrebe, datum vjenčanja, lokaciju...' 
                : 'Describe your needs, wedding date, location...'}
              value={form.message}
              onChange={handleChange}
              required
              className="w-full px-5 py-4 bg-[#fcfaf8] border border-[#ede3da] rounded-2xl focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-transparent transition-all text-gray-900 placeholder-gray-400 resize-none"
            ></textarea>
          </div>

          {status.message && (
            <div className={`p-5 rounded-2xl text-center font-bold ${
              status.type === 'success' 
                ? 'bg-emerald-50 text-emerald-700 border-2 border-emerald-200' 
                : 'bg-red-50 text-red-700 border-2 border-red-200'
            }`}>
              {status.message}
            </div>
          )}

          <div className="flex justify-center pt-8">
            <button 
              type="submit" 
              disabled={sending}
              className="text-gray-900 px-12 py-4 font-bold tracking-[0.3em] uppercase hover:opacity-50 transition-all border-b border-gray-900 text-xs disabled:opacity-30"
            >
              {sending 
                ? (currentLang === 'bs' ? 'Slanje...' : 'Sending...') 
                : (currentLang === 'bs' ? 'Pošalji poruku' : 'Send message')} &rarr;
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ContactForm;